import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import {
  EuiFlexGrid,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHealth,
  EuiLink,
} from '@elastic/eui';

import { FlexItemContent } from '../../../components/FlexItemContent';
import { convertEpochToDate } from '../../../utils';
import { ClusterFeature } from '../ClusterFeature';
import { useClusterData } from './ClusterDataProvider';
import { ErrorDisplay } from '../../../components/ErrorDisplay';
import ElementErrorBoundary from '../../../components/ErrorBoundary/ElementErrorBoundary';

const COMPONENT_ID = 'cluster-overview';

export const ClusterOverview = forwardRef((props, ref) => {
  const [clusterDetails, setClusterDetails] = useState({});
  const { stackName, clusterId, clusterSyncStatus, showStackDetailsModal } = props;
  const { clusterData, error, isInitialLoading, refetch, isComponentRefreshing } = useClusterData();

  const isLoading = isInitialLoading || isComponentRefreshing(COMPONENT_ID);

  useImperativeHandle(ref, () => ({
    refreshClusterData: async () => {
      if (refetch) {
        try {
          await refetch(COMPONENT_ID);
        } catch (error) {
          console.error('Failed to refresh cluster data:', error);
        }
      }
    }
  }));

  const processClusterDescription = (clusterDescription) => {
    if (!clusterDescription) return null;

    const mutableClusterDescription = JSON.parse(JSON.stringify(clusterDescription));
    const { createdBy, creationTime, lastModificationTime } = mutableClusterDescription.auditProperties;

    try {
      const parsedCreator = JSON.parse(createdBy);
      if (typeof parsedCreator !== 'object') throw new Error('Not Object');
      mutableClusterDescription.auditProperties.createdBy = parsedCreator;
    } catch (e) {
      mutableClusterDescription.auditProperties.createdBy = { id: createdBy };
    }

    mutableClusterDescription.auditProperties.creationTime = convertEpochToDate(creationTime);
    mutableClusterDescription.auditProperties.lastModificationTime = convertEpochToDate(lastModificationTime);

    return mutableClusterDescription;
  };

  useEffect(() => {
    if (clusterData) {
      const processed = processClusterDescription(clusterData);
      if (processed) {
        setClusterDetails(processed);
      }
    }
  }, [clusterData]);

  const renderStack = () => {
    if (!clusterDetails?.cluster) return '';
    const { cluster: { stackName: clusterStackName } } = clusterDetails;
    return <EuiLink onClick={() => showStackDetailsModal()}>{clusterStackName}</EuiLink>;
  };

  const renderCluster = () => {
    if (!clusterDetails?.cluster) return '';
    const { cluster: { clusterId: clusterClusterId } } = clusterDetails;
    return clusterClusterId;
  };

  const renderCreationTime = () => {
    if (!clusterDetails?.auditProperties) return '';
    const { auditProperties: { creationTime } } = clusterDetails;
    return creationTime;
  };

  const renderCreatedByAccount = () => {
    if (!clusterDetails?.auditProperties?.createdBy) return '';
    const { auditProperties: { createdBy: { id } } } = clusterDetails;
    return id;
  };

  const renderLastModifiedBy = () => {
    if (!clusterDetails?.auditProperties) return '';
    const { auditProperties: { lastModifiedBy } } = clusterDetails;
    return lastModifiedBy;
  };

  const renderLastModificationTime = () => {
    if (!clusterDetails?.auditProperties) return '';
    const { auditProperties: { lastModificationTime } } = clusterDetails;
    return lastModificationTime;
  };

  const renderCreatedByServicePrinciple = () => {
    if (!clusterDetails?.auditProperties?.createdBy?.sp) return '--';
    const { auditProperties: { createdBy: { sp } } } = clusterDetails;
    if (!sp || sp.length === 0) {
      return '--';
    }
    return (
      <React.Fragment>
        {sp.map((item) => (
          <div key={item}>{item}</div>
        ))}
        <br />
      </React.Fragment>
    );
  };

  const renderClusterSyncStatus = () => {
    if (clusterSyncStatus === 'loading') {
      return null;
    } else if (clusterSyncStatus === true) {
      return <EuiHealth color={'green'}>{'Cluster In Sync'}</EuiHealth>;
    } else {
      return <EuiHealth color={'red'}>{'Cluster Not In Sync'}</EuiHealth>;
    }
  };

  if (error) {
    return <ErrorDisplay />;
  }

  return (
    <ElementErrorBoundary key={'cluster-overview-inner-content'}>
      <EuiFlexGrid
        columns={4}
        gutterSize="l"
        style={{ border: 'none' }}
        direction="row"
      >
        <EuiFlexItem key="1">
          <FlexItemContent
            title={'Stack Name'}
            description={renderStack}
            isLoading={isLoading}
          />
        </EuiFlexItem>
        <EuiFlexItem key="2">
          <FlexItemContent
            title={'Cluster ID'}
            description={renderCluster}
            isLoading={isLoading}
          />
        </EuiFlexItem>
        <EuiFlexItem key="3">
          <FlexItemContent
            title={'Creation Time'}
            description={renderCreationTime}
            isLoading={isLoading}
          />
        </EuiFlexItem>
        <EuiFlexItem key="4">
          <FlexItemContent
            title={'Created By (AWS Account)'}
            description={renderCreatedByAccount}
            isLoading={isLoading}
          />
        </EuiFlexItem>
        <EuiFlexItem key="5">
          <FlexItemContent
            title={'Created By (Service Principle)'}
            description={renderCreatedByServicePrinciple}
            isLoading={isLoading}
          />
        </EuiFlexItem>
        <EuiFlexItem key="6">
          <FlexItemContent
            title={'Last Modification Time'}
            description={renderLastModificationTime}
            isLoading={isLoading}
          />
        </EuiFlexItem>
        <EuiFlexItem key="7">
          <FlexItemContent
            title={'Last Modified By'}
            description={renderLastModifiedBy}
            isLoading={isLoading}
          />
        </EuiFlexItem>
        <EuiFlexItem key="8">
          <FlexItemContent
            title={'Cluster Sync Status'}
            description={renderClusterSyncStatus}
            isLoading={clusterSyncStatus === 'loading'}
            isError={clusterSyncStatus === 'error'}
          />
        </EuiFlexItem>
        <EuiFlexItem key="9">
          <FlexItemContent
            title={'Feature Tags'}
            description={
              <ClusterFeature
                stackName={stackName}
                clusterId={clusterId}
              />
            }
          />
        </EuiFlexItem>
      </EuiFlexGrid>
      <br />
    </ElementErrorBoundary>
  );
});
